import { gql, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { FileDocumentObjectTypes, FileDocumentTypes } from '@generated/graphql';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { PdfViewer } from '@ovComponents/2-component/pdfViewer/pdfViewer';
import { useFileUpload } from '@providers/fileUploadHook';
import { UserContext } from '@providers/userContextProvider';
import { FlagsmithFlags } from '@utils/flagsmith';
import { safeWindowOpen } from '@utils/urlSanitization';
import { Box, Button, CircularProgress, useToast } from 'hephaestus/web';
import { Download, Upload } from 'hephaestus/webIcons';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { FETCH_FORM_COMPLETIONS } from './manageFormCompletions.queries';
import { unsignedOverwriteFileName } from './unsignedOverwriteFileName';

const FETCH_FORM_COMPLETION = gql`
  query fetchFormCompletion($formCompletionId: ObjectID!) {
    fetchFormCompletion(formCompletionId: $formCompletionId) {
      id
      translatedName { ${TRANSLATED_STRING_FIELDS} }
      resolvedPdfFileName { ${TRANSLATED_STRING_FIELDS} }
      templateType
      unsignedUrl
      state
      unsignedDocument {
        id
        downloadUrl
        fileName
        s3Key
      }
    }
  }
`;

const ARCHIVE_FORM_COMPLETION = gql`
  mutation archiveFormCompletion($input: ArchiveFormCompletionInput!) {
    archiveFormCompletion(input: $input) {
      formCompletion {
        id
        state
      }
    }
  }
`;

const DELETE_FILE_DOCUMENT = gql`
  mutation deleteFileDocument($input: DeleteFileDocumentInput!) {
    deleteFileDocument(input: $input) {
      fileDocument {
        id
      }
    }
  }
`;

export const ViewFormDialog = ({
  formCompletionId,
  onClose,
  onArchive,
  canArchive,
}: {
  formCompletionId: string;
  onClose: () => void;
  onArchive?: () => void;
  canArchive: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { enqueueToast } = useToast();
  const [isArchiving, setIsArchiving] = useState(false);
  const { uploadFile } = useFileUpload();
  const { activeOrganization } = useContext(UserContext);
  const overwriteReuploadEnabled = useFeatureFlag(
    FlagsmithFlags.form_completion_reupload_overwrite,
  );

  const [pdfRetryCount, setPdfRetryCount] = useState(0);
  const maxRetries = 3;

  const { data, loading, refetch } = useQuery(FETCH_FORM_COMPLETION, {
    variables: { formCompletionId },
    fetchPolicy: 'no-cache',
  });

  // Handle PDF load error with retry
  const handlePdfError = async () => {
    if (pdfRetryCount < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await refetch();
      setPdfRetryCount((prev) => prev + 1);
    } else {
      enqueueToast(t('workflowCompletions:formPdfLoadError'), {
        variant: 'error',
      });
    }
  };

  // Reset retry count when dialog opens with new form
  useEffect(() => {
    setPdfRetryCount(0);
  }, [formCompletionId]);

  const [archiveFormCompletion] = useMutation(ARCHIVE_FORM_COMPLETION);
  const [deleteFileDocument] = useMutation(DELETE_FILE_DOCUMENT);
  const [pdfViewerKey, setPdfViewerKey] = useState(0);

  const reuploadFile = async (file: File) => {
    try {
      const formCompletion = data.fetchFormCompletion;
      const uploadFileName = overwriteReuploadEnabled
        ? unsignedOverwriteFileName(
            formCompletion.id,
            formCompletion.unsignedDocument?.s3Key,
          ) ||
          formCompletion.unsignedDocument?.fileName ||
          file.name
        : file.name;
      const s3Key = await uploadFile(file, {
        objectType: FileDocumentObjectTypes.FormCompletionUnsigned,
        objectId: formCompletion.id,
        sourceId: formCompletion.id,
        fileName: uploadFileName,
        mediaType: file.type,
        sourceType: 'FORM_COMPLETION',
        type: FileDocumentTypes.Template,
        permissionType: 'PUBLIC',
        name: uploadFileName,
        organizationId: activeOrganization?.id ?? '',
      });
      if (overwriteReuploadEnabled) {
        if (!s3Key) {
          enqueueToast(t('workflowCompletions:formReuploadError'), {
            variant: 'error',
          });
          return;
        }
        await refetch();
        setPdfViewerKey((key) => key + 1);
      } else {
        if (formCompletion.unsignedDocument?.id) {
          await deleteFileDocument({
            variables: {
              input: {
                fileDocumentId: formCompletion.unsignedDocument.id,
              },
            },
          });
        }
        await refetch();
      }
      enqueueToast(t('workflowCompletions:formReuploaded'), {
        variant: 'success',
      });
    } catch {
      enqueueToast(t('workflowCompletions:formReuploadError'), {
        variant: 'error',
      });
    }
  };

  const handleArchive = async () => {
    try {
      setIsArchiving(true);
      await archiveFormCompletion({
        variables: {
          input: {
            formCompletionId,
          },
        },
        refetchQueries: [FETCH_FORM_COMPLETIONS],
      });
      enqueueToast(t('workflowCompletions:formArchived'), {
        variant: 'success',
      });
      onArchive?.();
      onClose();
    } catch {
      enqueueToast(t('workflowCompletions:formArchiveError'), {
        variant: 'error',
      });
    } finally {
      setIsArchiving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const formCompletion = data?.fetchFormCompletion;

  if (!formCompletion) {
    return null;
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle onClose={onClose}>
        {translateBackendString(formCompletion.resolvedPdfFileName) ||
          translateBackendString(formCompletion.translatedName)}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {formCompletion.templateType === 'PDF' &&
            (formCompletion.unsignedDocument?.downloadUrl ||
              formCompletion.unsignedUrl) && (
              <Box
                sx={{
                  width: '100%',
                }}
              >
                <PdfViewer
                  key={overwriteReuploadEnabled ? pdfViewerKey : undefined}
                  pdfUrl={
                    formCompletion.unsignedDocument?.downloadUrl ||
                    formCompletion.unsignedUrl
                  }
                  fullWidth
                  onError={handlePdfError}
                />
              </Box>
            )}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {formCompletion.state !== 'ARCHIVED' && canArchive && (
            <Button
              variant='contained'
              color='error'
              onClick={handleArchive}
              disabled={isArchiving}
            >
              {t('shared:archive')}
            </Button>
          )}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}
          >
            <Button
              variant='contained'
              startIcon={<Download />}
              onClick={() =>
                safeWindowOpen(
                  formCompletion.unsignedDocument?.downloadUrl ||
                    formCompletion.unsignedUrl,
                  {
                    target: '_blank',
                    onError: (errorMsg) => {
                      enqueueToast(errorMsg, { variant: 'error' });
                    },
                  },
                )
              }
              color='secondary'
            >
              {t('shared:download')}
            </Button>
            <Button
              startIcon={<Upload />}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf';
                input.onchange = (e) => {
                  const f = (e.target as HTMLInputElement).files?.[0];
                  if (f) {
                    reuploadFile(f);
                  }
                };
                input.click();
              }}
            >
              {t('shared:reupload')}
            </Button>
          </Box>
        </Box>
      </DialogFooter>
    </Dialog>
  );
};
